/**
 * referrals/ReferralsTab.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Referrals dashboard — shows the user's referral link, stats, and
 * commission history.
 *
 * FEATURES:
 *   - Unique referral link with copy-to-clipboard button
 *   - Stats: total referred users, total commissions earned, pending commissions
 *   - List of referred users with their signup date and plan
 *   - Commission history with payment status
 *   - Referral link generates a ?ref=<code> URL for sharing
 *
 * PROPS (from ProfilePage sharedProps):
 *   user {object} — current user from AuthContext
 *
 * BACKEND ENDPOINTS USED:
 *   GET /api/referrals/stats/         — referral summary stats
 *   GET /api/referrals/referred-users/ — list of users this person referred
 *   GET /api/referrals/commissions/    — commission history
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback } from 'react';
import styles from './ReferralsTab.module.css';
import apiService from '../../../services/api';
import { useAuthContext } from '../../../context/AuthContext';

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = ['All Referrals', 'Earnings', 'Payouts', 'How it Works'];

// ─── Premium type badge ───────────────────────────────────────────────────────
function PremiumBadge({ type }) {
  const cfg = {
    Business: { bg: '#eef0ff', color: '#6c5ce7', icon: '🏢' },
    Pro:      { bg: '#e8f5e9', color: '#2e7d32', icon: '⚡' },
    Basic:    { bg: '#e3f2fd', color: '#1565c0', icon: '🔵' },
    Standard: { bg: '#e0faf0', color: '#00b894', icon: '⭐' },
    Free:     { bg: '#f1f3f7', color: '#636e72', icon: '🆓' },
  }[type] || { bg: '#f1f3f7', color: '#636e72', icon: '—' };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: cfg.bg, color: cfg.color,
      padding: '3px 10px', borderRadius: 14,
      fontSize: 12, fontWeight: 700,
    }}>
      {cfg.icon} {type}
    </span>
  );
}

// ─── Not-verified gate ────────────────────────────────────────────────────────
function NotVerifiedGate() {
  return (
    <div className={styles.gateWrap}>
      <div className={styles.gateIcon}>🔒</div>
      <h2 className={styles.gateTitle}>Verification Required</h2>
      <p className={styles.gateSub}>
        You need to verify your account before you can participate in the
        referrals &amp; commissions programme.
      </p>
      <div className={styles.gateSteps}>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>1</span>
          <span>Go to <strong>Settings → Profile Settings</strong></span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>2</span>
          <span>Complete your profile and upload your ID documents</span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>3</span>
          <span>Submit for admin verification and wait for approval</span>
        </div>
      </div>
      <div className={styles.gateBadge}>
        Get verified to participate in referrals
      </div>
    </div>
  );
}

// ─── Not-enrolled gate (verified but referral_allowed is false / null) ─────────
function NotEnrolledGate() {
  return (
    <div className={styles.gateWrap}>
      <div className={styles.gateIcon}>📋</div>
      <h2 className={styles.gateTitle}>Enrol in the Referral Programme</h2>
      <p className={styles.gateSub}>
        Your account is verified, but you haven't enrolled in the Referral
        Programme yet. Enrol now to start earning commissions by referring
        friends and businesses.
      </p>
      <div className={styles.gateSteps}>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>1</span>
          <span>Go to <strong>Profile Settings → Referral Programme</strong></span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>2</span>
          <span>Fill in your enrolment details including your M-Pesa number</span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>3</span>
          <span>Submit and wait for admin approval — usually within 24 hrs</span>
        </div>
      </div>
      <div className={styles.gateBadge}>
        <span>🚀</span>
        Enroll yourself for referrals jobs in Referral Programme in profile setting
      </div>
    </div>
  );
}

// ─── Pending-approval gate (enrolled but awaiting admin sign-off) ──────────────
function PendingApprovalGate() {
  return (
    <div className={styles.gateWrap}>
      <div className={styles.gateIcon}>⏳</div>
      <h2 className={styles.gateTitle}>Application Under Review</h2>
      <p className={styles.gateSub}>
        Your application was submitted for approval — keep watch! Our team
        will review your enrolment and notify you once it's approved.
      </p>
      <div className={styles.gateSteps}>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNumDone}>✓</span>
          <span>Account verified</span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNumDone}>✓</span>
          <span>Referral Programme application submitted</span>
        </div>
        <div className={styles.gateStep}>
          <span className={styles.gateStepNum}>3</span>
          <span>Awaiting admin approval — check back soon</span>
        </div>
      </div>
      <div className={styles.gateBadge}
        style={{ background: 'linear-gradient(135deg,#f39c12,#fdcb6e)', boxShadow: '0 4px 16px rgba(243,156,18,0.3)' }}>
        <span>👀</span>
        Your application was submitted for approvals, keep watch!
      </div>
    </div>
  );
}

// ─── Avatar colour helper ─────────────────────────────────────────────────────
const AVATAR_COLORS = ['#6c5ce7', '#00b894', '#e17055', '#0984e3', '#fd79a8', '#fdcb6e'];
function avatarColor(idx) { return AVATAR_COLORS[idx % AVATAR_COLORS.length]; }

// ─── Main component ───────────────────────────────────────────────────────────
const ReferralsPage = () => {
  const { getToken } = useAuthContext();

  // ── Verification + referral-allowed status ────────────────────────────────
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [referralAllowed,    setReferralAllowed]    = useState(null); // null | false | 'pending' | true
  const [loadingStatus,      setLoadingStatus]      = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = getToken();
        const data  = await apiService.getMyProfile(token);
        setVerificationStatus(data?.verification_status || 'unverified');
        // referral_allowed: true = fully allowed, 'pending' = applied but not approved, false/null = not enrolled
        setReferralAllowed(data?.referral_allowed ?? false);
      } catch {
        setVerificationStatus('unverified');
        setReferralAllowed(false);
      } finally {
        setLoadingStatus(false);
      }
    };
    fetchStatus();
  }, []);

  // ── Referral data ──────────────────────────────────────────────────────────
  const [stats,         setStats]         = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [commissions,   setCommissions]   = useState([]);
  const [withdrawals,   setWithdrawals]   = useState([]);
  const [loadingData,   setLoadingData]   = useState(false);
  const [dataError,     setDataError]     = useState('');

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeTab,    setActiveTab]    = useState('All Referrals');
  const [copied,       setCopied]       = useState(false);
  const [withdrawState, setWithdrawState] = useState({});
  const [withdrawMsg,   setWithdrawMsg]   = useState('');

  // ── Fetch all referral data ────────────────────────────────────────────────
  const fetchReferralData = useCallback(async () => {
    setLoadingData(true);
    setDataError('');
    try {
      const token = getToken();
      const [statsRes, usersRes, commissionsRes, withdrawalsRes] = await Promise.all([
        apiService.getReferralStats(token),
        apiService.getReferredUsers(token),
        apiService.getReferralCommissions(token),
        apiService.getReferralWithdrawals(token),
      ]);
      setStats(statsRes);
      setReferredUsers(usersRes?.results    || []);
      setCommissions(commissionsRes?.results || []);
      setWithdrawals(withdrawalsRes?.results || []);
    } catch (err) {
      console.error('Referral data fetch error:', err);
      setDataError('Failed to load referral data. Please refresh.');
    } finally {
      setLoadingData(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (verificationStatus === 'verified' && referralAllowed === true) fetchReferralData();
  }, [verificationStatus, referralAllowed, fetchReferralData]);

  // ── Copy referral link ─────────────────────────────────────────────────────
  const handleCopy = () => {
    if (!stats?.referral_link) return;
    navigator.clipboard.writeText(stats.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Withdraw ───────────────────────────────────────────────────────────────
  const handleWithdraw = async (rowId) => {
    setWithdrawState(prev => ({ ...prev, [rowId]: 'processing' }));
    setWithdrawMsg('');
    try {
      const token = getToken();
      await apiService.requestReferralWithdrawal(token, {});
      setWithdrawState(prev => ({ ...prev, [rowId]: 'done' }));
      setWithdrawMsg('✅ Withdrawal initiated — M-Pesa payout is being processed.');
      fetchReferralData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Withdrawal failed.';
      setWithdrawState(prev => ({ ...prev, [rowId]: 'error' }));
      setWithdrawMsg(`❌ ${msg}`);
    }
  };

  // ── Loading splash ─────────────────────────────────────────────────────────
  if (loadingStatus) {
    return (
      <div className={styles.container} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320,
      }}>
        <div style={{ textAlign: 'center', color: '#636e72' }}>
          <div className={styles.loadingSpinner} />
          <p style={{ marginTop: 14, fontSize: 15 }}>Checking account status…</p>
        </div>
      </div>
    );
  }

  // ── Gate checks (in priority order) ───────────────────────────────────────
  if (verificationStatus !== 'verified') return <NotVerifiedGate />;
  if (referralAllowed === 'pending')     return <PendingApprovalGate />;
  if (referralAllowed !== true)          return <NotEnrolledGate />;

  // ── Stat cards from live data ──────────────────────────────────────────────
  const statsCards = stats ? [
    {
      icon: '👥', label: 'Total Referrals',
      value: String(stats.total_referrals ?? 0),
      sub: `↑ +${stats.active_referrals ?? 0} active`,
      color: '#6c5ce7',
    },
    {
      icon: '✅', label: 'Active Referrals',
      value: String(stats.active_referrals ?? 0),
      sub: stats.total_referrals > 0
        ? `◎ ${Math.round((stats.active_referrals / stats.total_referrals) * 100)}% approved`
        : '◎ 0% approved',
      color: '#00b894',
    },
    {
      icon: '💰', label: 'Total Earnings',
      value: `KSh ${Number(stats.total_earnings ?? 0).toLocaleString()}`,
      sub: `☐ Withdrawn: KSh ${Number(stats.withdrawn_total ?? 0).toLocaleString()}`,
      color: '#fdcb6e',
    },
    {
      icon: '📦', label: 'Pending Balance',
      value: `KSh ${Number(stats.pending_balance ?? 0).toLocaleString()}`,
      sub: '● Ready for payout',
      color: '#e17055', subColor: '#e17055',
    },
  ] : [];

  const refLink = stats?.referral_link || '';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.container}>

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>👥 Referrals &amp; Commissions</h1>
        </div>
      </div>

      {/* Stats cards */}
      {loadingData ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#636e72' }}>
          <div className={styles.loadingSpinner} style={{ margin: '0 auto' }} />
          <p style={{ marginTop: 12 }}>Loading your referral data…</p>
        </div>
      ) : dataError ? (
        <div style={{
          background: '#fff3f3', border: '1px solid #fca5a5', borderRadius: 12,
          padding: '16px 20px', color: '#dc2626', marginBottom: 24,
          fontSize: 14, fontWeight: 600,
        }}>
          {dataError}
          <button onClick={fetchReferralData} style={{
            marginLeft: 12, padding: '4px 12px', borderRadius: 8,
            border: '1px solid #dc2626', background: 'white',
            color: '#dc2626', cursor: 'pointer', fontSize: 13,
          }}>Retry</button>
        </div>
      ) : (
        <div className={styles.statsGrid}>
          {statsCards.map((s, i) => (
            <div key={i} className={styles.statCard}>
              <div className={styles.statIcon}
                style={{ background: `${s.color}20`, color: s.color }}>
                {s.icon}
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>{s.label}</span>
                <span className={styles.statValue}>{s.value}</span>
                <span className={styles.statSub}
                  style={s.subColor ? { color: s.subColor } : {}}>
                  {s.sub}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabBar}>
        <div className={styles.tabs}>
          {TABS.map(t => (
            <button key={t}
              className={`${styles.tab} ${activeTab === t ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(t)}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Referral link card */}
      <div className={styles.linkCard}>
        <div>
          <h3 className={styles.linkTitle}>Your Referral Link</h3>
          <p className={styles.linkSub}>
            This is your unique link — share it to earn commissions
          </p>
        </div>
        <div className={styles.linkInputGroup}>
          <input className={styles.linkInput} value={refLink || 'Loading…'} readOnly />
          <button className={styles.copyBtn} onClick={handleCopy}>
            {copied ? '✓ Copied' : '📋 Copy Link'}
          </button>
        </div>
      </div>

      {/* Withdrawal toast */}
      {withdrawMsg && (
        <div style={{
          padding: '12px 18px', borderRadius: 10, marginBottom: 16,
          background: withdrawMsg.startsWith('✅') ? '#e0faf0' : '#fff3f3',
          color:      withdrawMsg.startsWith('✅') ? '#00b894' : '#dc2626',
          fontSize: 14, fontWeight: 600,
          border: `1px solid ${withdrawMsg.startsWith('✅') ? '#6ee7b7' : '#fca5a5'}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{withdrawMsg}</span>
          <button onClick={() => setWithdrawMsg('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 18, color: 'inherit', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── ALL REFERRALS tab ── */}
      {activeTab === 'All Referrals' && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>USER</th>
                  <th>DATE JOINED</th>
                  <th>PLAN TYPE</th>
                  <th>YOUR COMMISSION</th>
                  <th>DATE POSTED</th>
                  <th>STATUS</th>
                  <th>PAYMENT STATUS</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{
                      textAlign: 'center', padding: '40px 20px',
                      color: '#9ca3af', fontSize: 14,
                    }}>
                      No referrals yet. Share your link above to get started!
                    </td>
                  </tr>
                ) : commissions.map((c, i) => {
                  const ws      = withdrawState[c.id] || 'idle';
                  const color   = avatarColor(i);
                  const commAmt = Number(c.commission_amount ?? 0);
                  const hasComm = commAmt > 0;
                  const isPaid  = c.status === 'paid';

                  // Derive referred user's overall active status from the
                  // referredUsers list (same user may have multiple commissions)
                  const referredEntry = referredUsers.find(
                    r => r.email === c.referred_user_email
                  );
                  const userActive = referredEntry?.is_active ?? (isPaid);
                  const rowStatus  = userActive ? 'Active' : 'Pending';

                  return (
                    <tr key={c.id}>
                      {/* USER */}
                      <td>
                        <div className={styles.userCell}>
                          <div className={styles.avatar}
                            style={{ background: `${color}25`, color }}>
                            {c.referred_user_initials || '??'}
                          </div>
                          <div>
                            <div className={styles.userName}>{c.referred_user_name}</div>
                            <div className={styles.userEmail}>{c.referred_user_email}</div>
                          </div>
                        </div>
                      </td>

                      {/* DATE JOINED */}
                      <td>
                        {c.created_at
                          ? new Date(c.created_at).toLocaleDateString('en-KE', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })
                          : '—'}
                      </td>

                      {/* PLAN TYPE — from mpesa_transaction.plan via serializer */}
                      <td>
                        <PremiumBadge type={c.plan_type || c.premium_type || referredEntry?.premium_type || 'Free'} />
                      </td>

                      {/* YOUR COMMISSION */}
                      <td>
                        <span style={{ fontWeight: 700, color: '#00b894' }}>
                          KSh {commAmt.toLocaleString()}
                        </span>
                        {hasComm && (
                          <span className={styles.pctBadge}>{c.commission_rate}%</span>
                        )}
                      </td>

                      {/* DATE POSTED — when the ad was posted (from mpesa_transaction.ad.created_at) */}
                      <td>
                        {c.ad_posted
                          ? new Date(c.ad_posted).toLocaleDateString('en-KE', {
                              day: 'numeric', month: 'short', year: 'numeric',
                            })
                          : '—'}
                      </td>

                      {/* USER STATUS */}
                      <td>
                        <span className={`${styles.statusBadge} ${
                          rowStatus === 'Active' ? styles.statusActive : styles.statusPending
                        }`}>
                          {rowStatus}
                        </span>
                      </td>

                      {/* PAYMENT STATUS — mirrors admin ReferredUsersPage Payment Status column */}
                      <td>
                        {isPaid ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          }}>💚 Paid</span>
                        ) : c.status === 'pending' ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                          }}>⏳ Processing</span>
                        ) : (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          }}>— Not Paid</span>
                        )}
                      </td>

                      <td><button className={styles.moreBtn}>⋮</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* How it works — always visible on this tab */}

          {/* How it works — always visible on this tab */}
          <div className={styles.howCard} style={{ marginTop: 24 }}>
            <h3 className={styles.howTitle}>ℹ️ How commissions work</h3>
            <div className={styles.howSteps}>
              <div className={styles.howStep}>
                <span className={styles.howIcon}>🎁</span>
                <div><strong>Invite Friends</strong><br />Share your unique referral link</div>
              </div>
              <div className={styles.howStep}>
                <span className={styles.howIcon}>💵</span>
                <div><strong>They Purchase</strong><br />When they buy a premium ad plan</div>
              </div>
              <div className={styles.howStep}>
                <span className={styles.howIcon}>🤑</span>
                <div>
                  <strong>You Get Paid</strong><br />
                  Earn <span className={styles.highlight}>30%</span> — paid to your M-Pesa
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── EARNINGS tab ── */}
      {activeTab === 'Earnings' && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>USER</th>
                <th>DATE</th>
                <th>TRANSACTION</th>
                <th>RATE</th>
                <th>COMMISSION</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {commissions.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{
                    textAlign: 'center', padding: '40px 20px',
                    color: '#9ca3af', fontSize: 14,
                  }}>
                    No commissions earned yet.
                  </td>
                </tr>
              ) : commissions.map((c, i) => (
                <tr key={c.id}>
                  <td>
                    <div className={styles.userCell}>
                      <div className={styles.avatar}
                        style={{ background: `${avatarColor(i)}25`, color: avatarColor(i) }}>
                        {c.referred_user_initials || '??'}
                      </div>
                      <div>
                        <div className={styles.userName}>{c.referred_user_name}</div>
                        <div className={styles.userEmail}>{c.referred_user_email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {c.created_at
                      ? new Date(c.created_at).toLocaleDateString('en-KE', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td>KSh {Number(c.transaction_amount).toLocaleString()}</td>
                  <td>{c.commission_rate}%</td>
                  <td style={{ fontWeight: 700, color: '#00b894' }}>
                    KSh {Number(c.commission_amount).toLocaleString()}
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${
                      c.status === 'paid' ? styles.statusActive : styles.statusPending
                    }`}>
                      {c.status === 'paid' ? 'Paid' : c.status === 'pending' ? 'Pending' : c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── PAYOUTS tab ── */}
      {activeTab === 'Payouts' && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DATE</th>
                <th>AMOUNT</th>
                <th>PHONE</th>
                <th>M-PESA RECEIPT</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {withdrawals.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{
                    textAlign: 'center', padding: '40px 20px',
                    color: '#9ca3af', fontSize: 14,
                  }}>
                    No withdrawals yet.
                  </td>
                </tr>
              ) : withdrawals.map(w => (
                <tr key={w.id}>
                  <td>
                    {new Date(w.initiated_at).toLocaleDateString('en-KE', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td style={{ fontWeight: 700 }}>KSh {Number(w.amount).toLocaleString()}</td>
                  <td>{w.phone}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {w.mpesa_receipt || '—'}
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${
                      w.status === 'completed' ? styles.statusActive : styles.statusPending
                    }`}>
                      {w.status === 'completed' ? 'Completed'
                        : w.status === 'processing' ? 'Processing'
                        : w.status === 'failed' ? 'Failed'
                        : w.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── HOW IT WORKS tab ── */}
      {activeTab === 'How it Works' && (
        <div className={styles.howCard}>
          <h3 className={styles.howTitle}>ℹ️ How commissions work</h3>
          <div className={styles.howSteps}>
            <div className={styles.howStep}>
              <span className={styles.howIcon}>🎁</span>
              <div><strong>Invite Friends</strong><br />Share your referral link</div>
            </div>
            <div className={styles.howStep}>
              <span className={styles.howIcon}>💵</span>
              <div><strong>They Purchase</strong><br />When they buy a premium plan</div>
            </div>
            <div className={styles.howStep}>
              <span className={styles.howIcon}>🤑</span>
              <div>
                <strong>You Get Paid</strong><br />
                Earn <span className={styles.highlight}>30%</span> of their payment,
                withdrawn via M-Pesa
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReferralsPage;

